import { DYEBATH_AGEING, type ExperimentStepKey } from '@dyeatlas/shared'

/**
 * The wizard is described as data rather than six hand-written forms: every step
 * lays out the same two-column grid, so only the field list differs between them.
 * `vocabulary` names a list from `useMasterData` storing ids, `choiceList` one
 * storing names, and `choices` is a fixed enum.
 */
export interface FieldDef {
  name: string
  label: string
  kind: 'text' | 'textarea' | 'number' | 'date' | 'select' | 'multiselect' | 'ratio'
  required?: boolean
  /** Which controlled list from `useMasterData` populates this control. */
  vocabulary?:
    | 'dyeSources'
    | 'manuscripts'
    | 'waterTypes'
    | 'fibers'
    | 'yarnTypes'
    | 'mordants'
    | 'institutions'
  /** An admin-managed list from `useMasterData` whose records keep the chosen name. */
  choiceList?:
    | 'weaveStructures'
    | 'preTreatments'
    | 'dryingMethods'
    | 'extractionMethods'
    | 'filtrationMethods'
    | 'illuminants'
    | 'observers'
  choices?: readonly string[]
  suffix?: string
  hint?: string
  placeholder?: string
  /** Full-width inside the two-column grid. */
  wide?: boolean
}

/** Shared by the experiment wizard and the reference form, directly under the mordant picker. */
export const MORDANT_NOTES: FieldDef = {
  name: 'morMordantNotes',
  label: 'Mordant composition notes',
  kind: 'textarea',
  wide: true,
  placeholder: 'e.g. Tin liquor: tin dissolved in aqua regia',
  hint: 'Optional. The specific composition or preparation of a mordant.',
}

export const STEP_FIELDS: Record<Exclude<ExperimentStepKey, 'review'>, FieldDef[]> = {
  general: [
    {
      name: 'title',
      label: 'Experiment title',
      kind: 'text',
      required: true,
      wide: true,
      placeholder: 'Madder on wool, alum mordant',
    },
    { name: 'manuscriptId', label: 'Manuscript', kind: 'select', vocabulary: 'manuscripts' },
    { name: 'recipeNumber', label: 'Recipe number', kind: 'text', placeholder: 'No. 42' },
    { name: 'experimentDate', label: 'Date of experiment', kind: 'date', required: true },
    { name: 'researcherName', label: 'Researcher / author', kind: 'text', required: true },
    { name: 'institutionId', label: 'Institution', kind: 'select', vocabulary: 'institutions' },
    { name: 'placeOfExperiment', label: 'Place of experiment', kind: 'text' },
    {
      name: 'notes',
      label: 'Notes',
      kind: 'textarea',
      wide: true,
      hint: 'Anything about the run that the structured fields do not capture.',
    },
  ],

  textile: [
    { name: 'texFiberId', label: 'Fiber', kind: 'select', vocabulary: 'fibers', required: true },
    { name: 'texYarnTypeId', label: 'Yarn type', kind: 'select', vocabulary: 'yarnTypes' },
    { name: 'texWeave', label: 'Weave structure', kind: 'select', choiceList: 'weaveStructures' },
    { name: 'texFabricWeight', label: 'Fabric weight', kind: 'number', suffix: 'g/m²' },
    {
      name: 'texSampleWeightDry',
      label: 'Sample weight (dry)',
      kind: 'number',
      suffix: 'g',
      required: true,
    },
    {
      name: 'texSampleDimensions',
      label: 'Sample dimensions',
      kind: 'text',
      placeholder: '10 × 10 cm',
    },
    { name: 'texPreTreatment', label: 'Pre-treatment', kind: 'select', choiceList: 'preTreatments' },
    { name: 'texPreTreatmentDetails', label: 'Pre-treatment details', kind: 'text' },
  ],

  mordanting: [
    {
      name: 'morMordantIds',
      label: 'Mordants',
      kind: 'multiselect',
      vocabulary: 'mordants',
      required: true,
      wide: true,
      hint: 'Select every mordant used, in the order applied.',
    },
    MORDANT_NOTES,
    { name: 'morConcentrationOwf', label: 'Mordant concentration', kind: 'number', suffix: '% owf' },
    {
      name: 'morWaterTypeId',
      label: 'Water type',
      kind: 'select',
      vocabulary: 'waterTypes',
      required: true,
    },
    {
      name: 'morLiquorRatio',
      label: 'Ratio dye-fabric',
      kind: 'ratio',
      required: true,
      placeholder: '20:1',
    },
    { name: 'morStartingPh', label: 'Starting pH', kind: 'number', required: true },
    { name: 'morPhAdjustments', label: 'pH adjustments', kind: 'text' },
    { name: 'morMaxTempC', label: 'Max. temperature', kind: 'number', suffix: '°C' },
    { name: 'morBoilingMin', label: 'Boiling time', kind: 'number', suffix: 'min' },
    { name: 'morSimmeringMin', label: 'Simmering time', kind: 'number', suffix: 'min' },
    { name: 'morRinseCount', label: 'Rinsing after mordanting', kind: 'number', suffix: '×' },
    { name: 'morRinseWaterTypeId', label: 'Rinsing water', kind: 'select', vocabulary: 'waterTypes' },
    { name: 'morRinseTempC', label: 'Rinsing temperature', kind: 'number', suffix: '°C' },
    { name: 'morDryingAfter', label: 'Drying after mordanting', kind: 'select', choiceList: 'dryingMethods' },
  ],

  dyebath: [
    {
      name: 'dyeSourceIds',
      label: 'Dye sources',
      kind: 'multiselect',
      vocabulary: 'dyeSources',
      required: true,
      wide: true,
      hint: 'Select every dye used, main dye first.',
    },
    { name: 'dyeExtractionMethod', label: 'Extraction method', kind: 'select', choiceList: 'extractionMethods' },
    { name: 'dyeMaterialAmountOwf', label: 'Dye material amount', kind: 'number', suffix: '% owf' },
    {
      name: 'dyeWaterTypeId',
      label: 'Water type',
      kind: 'select',
      vocabulary: 'waterTypes',
      required: true,
    },
    {
      name: 'dyeLiquorRatio',
      label: 'Ratio dye-fabric',
      kind: 'ratio',
      required: true,
      placeholder: '30:1',
    },
    { name: 'dyeStartingPh', label: 'Starting pH', kind: 'number' },
    { name: 'dyePhAdjustments', label: 'pH adjustments', kind: 'text' },
    { name: 'dyeMaxTempC', label: 'Max. temperature', kind: 'number', suffix: '°C', required: true },
    { name: 'dyeBoilingMin', label: 'Boiling time', kind: 'number', suffix: 'min' },
    { name: 'dyeSimmeringMin', label: 'Simmering time', kind: 'number', suffix: 'min' },
    { name: 'dyeAgeing', label: 'Dye bath ageing', kind: 'select', choices: DYEBATH_AGEING },
    { name: 'dyeFiltration', label: 'Filtration', kind: 'select', choiceList: 'filtrationMethods' },
  ],

  aftertreatment: [
    { name: 'aftRinseCount', label: 'Rinsing after dyeing', kind: 'number', suffix: '×' },
    { name: 'aftRinseWaterTypeId', label: 'Rinsing water', kind: 'select', vocabulary: 'waterTypes' },
    { name: 'aftRinseTempC', label: 'Rinsing temperature', kind: 'number', suffix: '°C' },
    { name: 'aftFinalRinsePh', label: 'Final rinse pH', kind: 'number' },
    { name: 'aftDryingMethod', label: 'Drying method', kind: 'select', choiceList: 'dryingMethods' },
    {
      name: 'aftDryingConditions',
      label: 'Drying conditions',
      kind: 'text',
      placeholder: 'Shade, 20 °C, 55% RH',
    },
  ],

  results: [
    { name: 'resLabL', label: 'CIELAB L*', kind: 'number', required: true, hint: '0 (black) to 100 (white)' },
    { name: 'resLabA', label: 'CIELAB a*', kind: 'number', required: true, hint: 'green (−) to red (+)' },
    { name: 'resLabB', label: 'CIELAB b*', kind: 'number', required: true, hint: 'blue (−) to yellow (+)' },
    { name: 'resIlluminant', label: 'Illuminant', kind: 'select', choiceList: 'illuminants' },
    { name: 'resObserver', label: 'Standard observer', kind: 'select', choiceList: 'observers' },
    { name: 'resInstrument', label: 'Instrument', kind: 'text', placeholder: 'Konica Minolta CM-700d' },
    { name: 'resMeasurementDate', label: 'Measurement date', kind: 'date' },
    { name: 'resAdditionalNotes', label: 'Additional notes', kind: 'textarea', wide: true },
  ],
}
